//import css from './css.module.less';
import Layout from '@/src/layout/EditorPageLayout/EditorPageLayout';
import qs from 'query-string';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Form } from 'src/components/Antd';
import RouteBlock from 'src/constrains/RouterBlock/RouterBlock';
import { useI18n } from 'src/hooks/useRouterChange';
import { getFilterOptions } from 'src/request/customer/list';
import { getTag, postTag } from 'src/request/customer/tag';
import { NEW } from 'src/tools/constant';
import { isUserValid, storeResourceNameGenerator } from 'src/tools/getUserInfo';
import { onFinishFailed, timeoutScroll } from 'src/tools/helper';
import { msg } from 'src/tools/message';
import { responseHandler } from 'src/tools/server';
import { i18nGet } from 'src/tools/tools';
import { ETagType } from 'src/types/common/customerDb';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from 'src/types/common/resources';
import { modal } from 'src/utils/Modal';
import TopLeft from './Components/TopLeft';
import TopRight from './Components/TopRight';
import Filter from './Filters';
import Setting from './Settings';
import { switchUuidHooks } from './hooks';
import { convertDataFromServer, convertDataFromState } from './tools';
import { generatePromotions } from 'src/pages/CustomerDatabaseConfig/tools';

const permissions = () => {
  const canEdit = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Customers,
        subCategory: EnumSubCategory.CustomerTagDetailsPage,
        operation: EnumOperation.edit_tag,
      }),
    ],
  });
  return { canEdit };
};
export const CustomerTagContext = React.createContext(null);
const Provider = (props) => {
  const { children } = props;
  const [hasModified, setHasModified] = React.useState(false);
  return (
    <CustomerTagContext.Provider
      value={{
        hasModified,
        setHasModified,
      }}>
      {children}
    </CustomerTagContext.Provider>
  );
};

const TagDetail = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { canEdit } = permissions();
  const { hasModified, setHasModified } = React.useContext(CustomerTagContext);
  const m: any = useLocation();
  const { count } = qs.parse(m.search);
  const { tagUuid, customerDatabaseUuid }: any = useParams();
  const [form] = Form.useForm();
  // const [hasModified, setHasModified] = React.useState(false);
  const [data, setData] = React.useState<any>({ customerDatabaseUuid });
  const [loaded, setLoaded] = React.useState(false);
  const [options, setOptions] = React.useState<any>({});
  const { uuid } = data || {};
  switchUuidHooks({ uuid });

  const _convertDataFromServer = (res) => {
    const response = convertDataFromServer(res);
    const { conditions, title, tagType } = response;
    form.setFieldsValue({
      conditions,
      title,
      tagType,
    });
  };
  const isNew = tagUuid === NEW;
  React.useEffect(() => {
    if (tagUuid !== NEW) {
      getTag(tagUuid)
        .then((res) => {
          setData(res);
          return res;
        })
        .then((res: any) => {
          _convertDataFromServer(res);
          setLoaded(true);
        });
    } else {
      form.setFieldsValue({
        title: `Tag ${count || ''}`,
        tagType: ETagType.Manual,
      });
      setLoaded(true);
    }
    getFilterOptions(customerDatabaseUuid).then((res: any) => {
      const {
        availableConsentAttributes,
        availableLanguages,
        availableDiscounts,
        availableSalesChannels,
        reportingCurrencyCode,
        salesChannelTacList,
      } = res || {};
      const consentAttribute = (availableConsentAttributes || {}).map((i) => {
        return { label: i, value: i };
      });
      const languages = (availableLanguages || {}).map((i) => {
        return { label: i.name, value: i.uuid };
      });

      const promotions = generatePromotions(availableDiscounts, getI18n);

      const salesChannels = (availableSalesChannels || []).map((i) => {
        return { label: i.name, value: i.uuid };
      });

      setOptions({
        currency: reportingCurrencyCode,
        languages,
        consentAttribute,
        promotions,
        salesChannels,
        salesChannelTacList,
      });
    });
  }, []);

  const ele = [
    { child: <Setting disabled={!canEdit} /> },
    {
      child: <Filter form={form} options={options} disabled={!canEdit} />,
    },
  ];

  const handleOnSubmit = (values) => {
    const { uuid, customerDatabaseUuid } = data;
    const { conditions, tagType, title } = values;
    const _conditions = convertDataFromState(conditions);
    const d = {
      uuid,
      customerDatabaseUuid,
      conditions: _conditions,
      tagType,
      title,
    };

    postTag(d).then((res) => {
      const _response = responseHandler(res);
      const { success, data, errorCode, errMsg } = _response || {};

      if (success) {
        setData(data);
        _convertDataFromServer(data);
        setHasModified(false);
        msg.success(
          getI18n(
            isNew
              ? 'customerTag.customerTag_Detail.createTag'
              : 'customerTag.customerTag_Detail.taghasbeenupdated',
          ),
        );
      } else {
        if (errorCode == 119042) {
          form.setFields([
            {
              name: 'title',
              errors: [getI18n('customerTag.customerTag_Detail.duplicateTip')],
            },
          ]);
          timeoutScroll(`${formName}_title`);
          return;
        }

        modal.error({
          title: getI18n('general.requestError'),
          content: errMsg || getI18n('general.systemError,PleaseContactAdmin'),
          onCancel: () => {
            console.error('errMsg:', errMsg);
          },
          onCancelText: getI18n('general.antd.Uploader.ok'),
        });
        return;
      }
      // setData(res);
      // setHasModified(false);
      // msg.success('Tag has been updated');
    });
  };
  const formName = 'store-tag-editor';
  return (
    <React.Fragment>
      <RouteBlock
        onConfirm={(f) => f()}
        okText={getI18n('general.discard')}
        active={hasModified}
        title={getI18n(
          'customerTag.customerTag_Detail.discardallunsavedchanges',
        )}
        content={getI18n('customerTag.customerTag_Detail.leavepage')}
      />
      {loaded && (
        <Form
          form={form}
          scrollToFirstError
          name={formName}
          onValuesChange={() => {
            setHasModified(true);
          }}
          onFinishFailed={(data) => {
            onFinishFailed(formName, data);
          }}
          onFinish={handleOnSubmit}>
          <Layout
            topLeft={<TopLeft name={'test'} />}
            topRight={
              <TopRight
                onSubmit={() => {
                  form.submit();
                }}
                hasModified={hasModified && canEdit}
              />
            }
            elements={ele}
          />
        </Form>
      )}
    </React.Fragment>
  );
};

const _TagDetail = (props) => {
  return (
    <Provider>
      <TagDetail {...props} />
    </Provider>
  );
};

export default _TagDetail;

// post()
//   .then((res) => {
//     return abc;
//   })
//   .then(abc);
